import { createFileRoute, useParams } from '@tanstack/react-router'
import DialogPageComponent from '../../components/DialogPageComponent'
import { postGraphqlMutation, type TAPIPlace } from '@/contexts/graphql-and-swr-apis'
import { useWSPlaceUpdates } from '@/hooks/useWSPlaceUpdates'
import type { SlideInfo } from '@/hooks/useWSPlaceUpdates'
import LogicIf from '@/components/logic/LogicIf'
import { LargeSlideView } from '@/components/LargeSlideView'
import SlideHistorySidebar from '../../components/SlideHistorySidebar'
import SlideHistoryFullbar from '../../components/SlideHistoryFullbar'
import { useState, useEffect } from 'react'

export const Route = createFileRoute('/watch/$placeIdOrCode')({
  loader: async ({ params: { placeIdOrCode } }) => {
    // similar hook would be useGQL_placeByIdOrCode, but we don't want to use SWR here because we want to fetch the place info once on page load, and not re-fetch it every time the user navigates back to this page (which would happen if we used SWR). Instead, we just fetch it once here in the loader, and pass it down to the component as a prop.
    return await postGraphqlMutation<{ place: TAPIPlace }>(`
      query ($placeId: String!) {
        place(id: $placeId) {
          id
          name
          address
          latitude
          longitude
          googlePlaceId
          place_code
        }
      }
    `, { placeId: placeIdOrCode });
  },
  component: WatchPlace,
})

function WaitingForSlidesPlaceholder() {
  return (<div className="bg-gray-800 rounded-lg aspect-video flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-xl font-bold mb-2">Waiting for Slides</h2>
      <p className="text-gray-400">No slides received yet</p>
    </div>
  </div>);
}

/**
 * A page to watch live slides for a given place ID.
 *
 * Connects via WebSocket to receive live slide updates (and recent history via `since`),
 * displaying them in reverse chronological order alongside place information.
 */
function WatchPlace() {
  const { placeIdOrCode } = useParams({ from: '/watch/$placeIdOrCode' })
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const { place } = Route.useLoaderData();
  const myPlace = place ?? {} as TAPIPlace;
  const placeId = myPlace.id ?? placeIdOrCode; // Use resolved place ID if available, otherwise fallback to param (which may be a 6-8 digit code)

  // WebSocket connection — manages slide list internally, persists across reconnects
  const { slides, connectionStatus, isConnected, isConnecting, isForbidden } = useWSPlaceUpdates({
    placeId,
    enabled: true,
    since: 900, // pull up to 15 minutes of history on connect
  });

  const isLoading = isConnecting && slides.length === 0;
  const hasError = isForbidden || (connectionStatus === 'failed');
  const currentSlide = slides[currentSlideIndex] ?? null;

  // Handle keyboard navigation (up down left right arrows) to change the current slide index
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (slides.length === 0) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCurrentSlideIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCurrentSlideIndex((prev) => Math.min(prev + 1, slides.length - 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slides.length]);

  if (isLoading) {
    return (
      <DialogPageComponent>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold mb-2">Connecting to slides</h1>
          <p className="text-gray-600">Place ID: {placeId}</p>
          <p className="text-sm text-gray-500 mt-2">Status: {connectionStatus}</p>
        </div>
      </DialogPageComponent>
    )
  }

  if (hasError && slides.length === 0) {
    return (
      <DialogPageComponent>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">
            {isForbidden ? 'Access Denied' : 'Connection Error'}
          </h1>
          <p className="text-gray-600 mb-4">
            {`Unable to connect to place "${placeId}".`}
          </p>
          <a
            href="/watch/join"
            className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded transition duration-300"
          >
            Try Another Code
          </a>
        </div>
      </DialogPageComponent>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Mobile view: SlideHistoryFullbar only */}
      <div className="md:hidden">
        <SlideHistoryFullbar
          myPlace={myPlace}
          placeId={placeId}
          isConnected={isConnected}
          isLoading={isLoading}
          slides={slides}
          currentSlideIndex={currentSlideIndex}
          onSlideSelect={setCurrentSlideIndex}
        />
      </div>

      {/* Desktop view: Layout with Sidebar and LargeSlideView */}
      <div className="hidden md:flex md:flex-col md:h-screen md:overflow-hidden">
        <div className="flex flex-1 h-[calc(100vh-44px)]">
          {/* Slides history sidebar */}
          <SlideHistorySidebar
            myPlace={myPlace}
            placeId={placeId}
            isConnected={isConnected}
            isLoading={isLoading}
            slides={slides}
            currentSlideIndex={currentSlideIndex}
            onSlideSelect={setCurrentSlideIndex}
          />

          {/* Main slide display area */}
          <div className="flex-1 flex items-center justify-center p-0">
            <div className="w-full">
              <LogicIf condition={!!currentSlide}>
                <LargeSlideView slide={currentSlide} />
              </LogicIf>
              <LogicIf condition={!currentSlide}>
                <WaitingForSlidesPlaceholder />
              </LogicIf>
            </div>
          </div>
        </div>

        {/* Status bar (Desktop only) */}
        <div className="bg-gray-900 p-3 border-t border-gray-700 h-[44px]">
          <div className="max-w-6xl mx-auto text-center text-sm text-gray-400">
            {isConnected ? (
              "Watching for live slide updates..."
            ) : isLoading ? (
              "Connecting to place..."
            ) : (
              "Reconnecting..."
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
